import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './AppClerk';
import ErrorBoundary from './components/ErrorBoundary';
import { PostHogProvider, usePostHog } from 'posthog-js/react';

// Note: Stripe is now initialized dynamically in checkout components
// using the publishable key from the backend API

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// PostHog configuration
// API key: Set via REACT_APP_POSTHOG_KEY environment variable
// API host: Custom PostHog domain (set via REACT_APP_POSTHOG_HOST, defaults to custom domain)
// UI host: Always https://us.posthog.com (PostHog dashboard)
const posthogApiKey = process.env.REACT_APP_POSTHOG_KEY || '';
const posthogHost = process.env.REACT_APP_POSTHOG_HOST || 'https://khb3hj5543.genanything.ai';
const posthogUiHost = 'https://us.posthog.com';

// Log PostHog initialization errors only
if (!posthogApiKey) {
  console.error('❌ [PostHog] REACT_APP_POSTHOG_KEY not set - PostHog will not be initialized');
  console.error('❌ [PostHog] This means no events will be tracked. Check:');
  console.error('   1. Environment variable is set in Railway dashboard');
  console.error('   2. Dockerfile includes REACT_APP_POSTHOG_KEY in ARG/ENV');
  console.error('   3. A new build was triggered after setting the env var');
  console.error('   Note: React env vars must be set BEFORE building, not at runtime');
}

// Component to verify PostHog initialization
function PostHogVerification() {
  const posthog = usePostHog();
  
  useEffect(() => {
    if (!posthog) {
      console.error('❌ [PostHog] PostHog instance is null - events will not be tracked');
    } else if (typeof posthog.capture !== 'function') {
      console.error('❌ [PostHog] capture() method is NOT available');
    }
  }, [posthog]);
  
  return null;
}

const appContent = (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      {posthogApiKey ? (
        <PostHogProvider
          apiKey={posthogApiKey}
          options={{
            api_host: posthogHost,
            ui_host: posthogUiHost,
            capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
            debug: process.env.NODE_ENV === 'development',
            // Disable autocapture completely - autocapture records clicks, pageviews, and other interactions automatically
            // Without actions, this data is not queryable and often turns into noise (~85% of events)
            // We manually track all events except pageviews and add_payment_info to avoid paying for low value events
            autocapture: false,
            capture_pageview: false,
            // Session recording: Only record engaged sessions longer than 15 seconds to reduce costs
            // PostHog is expensive, so we filter out short/bounce sessions
            // strictMinimumDuration ensures the check is against actual buffered recording data
            // Note: minimumDuration is configured in PostHog dashboard (Settings > Session Replay)
            session_recording: {
              strictMinimumDuration: true, // Check against actual buffered recording data - ensures only sessions with sufficient continuous data are recorded
            },
            // Filter out Safari internal errors and other browser-specific errors
            before_send: (event) => {
              // Filter out Safari internal EmptyRanges error
              // Cast to any to access PostHog's internal exception properties
              const eventData = event as any;
              if (eventData?.$exception_message?.includes('EmptyRanges')) {
                return null; // Don't send this event
              }
              return event;
            },
          }}
        >
          <PostHogVerification />
          {appContent}
        </PostHogProvider>
      ) : (
        appContent
      )}
    </ErrorBoundary>
  </React.StrictMode>
);
